/* eslint-disable @typescript-eslint/no-unused-vars */
import {useAppSelector} from '@/redux-store/store';
import {Delete, PhotoCamera} from '@mui/icons-material';
import {
  Button,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Typography,
  Box,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  useTheme
} from '@mui/material';
import {Form, Formik} from 'formik';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AddModuleValidationSchema, {
  LABEL_MAX_LENGTH,
  URL_MAX_LENGTH
} from '@/schemas/admin/companies/manage-modules/add-module';
import {useDispatch} from 'react-redux';
import {CurrentModule} from '@/types/module';
import {
  initialState,
  resetCurrentModule,
  setCurrentModule
} from '@/redux-store/slices/modules';
import {SupportedLanguage} from '@/types/common';

import {generateRandomString} from '@/utils';

interface ModuleAddProps {
  onCancel?: () => void;
  onSubmit?: (newModule: CurrentModule) => void;
  onError?: (error: Error) => void;
  showOnlyTwoButtons?: boolean;
  companyId?: string;
}

const ModuleAdd: React.FC<ModuleAddProps> = ({
  onCancel,
  onError,
  onSubmit,
  showOnlyTwoButtons,
  companyId
}) => {
  const initialValues = useAppSelector((state) => state.modules.currentModule);
  const {t} = useTranslation();

  const theme = useTheme();

  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [labelLengthDE, setLabelLengthDE] = useState(
    initialValues.label.de.length
  );
  const [labelLengthEN, setLabelLengthEN] = useState(
    initialValues.label.en.length
  );

  const dispatch = useDispatch();

  const validationSchema = AddModuleValidationSchema(t);

  const resetFormData = useCallback(() => {
    dispatch(resetCurrentModule());
    setFileSizeError(null);
  }, [dispatch]);

  const handleImageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 0.5) {
          setFileSizeError(t('file_size_error', {maxSize: 500, unit: 'kB'}));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          dispatch(
            setCurrentModule({
              picture: reader.result as string
            })
          );
          setFileSizeError(null);
        };
      }
    },
    [dispatch, t]
  );

  const submitForm = useCallback(
    (values: CurrentModule) => {
      if (companyId) {
        const {url, picture, label} = values;
        const valuesToAdd: CurrentModule = {
          key: `${companyId}-${generateRandomString(8)}`,
          url: url,
          picture: picture,
          label: label
        };

        // addModule(valuesToAdd)
        //   .then((response) => {
        //     if (response) {
        //       if (onSubmit) {
        //         onSubmit(response);
        //         resetFormData();
        //       }
        //     } else {
        //       if (onError) {
        //         onError(new Error('Error while adding module'));
        //       }
        //     }
        //   })
        //   .catch((error) => {
        //     console.error('Error:', error);
        //     if (onError) {
        //       onError(error as Error);
        //     }
        //   });
      }
    },

    [companyId]
  );

  const handleDeleteImage = useCallback(() => {
    dispatch(setCurrentModule({picture: null}));
  }, [dispatch]);

  useEffect(() => {
    if (initialValues) {
      setLabelLengthDE(initialValues.label.de.length);
      setLabelLengthEN(initialValues.label.en.length);
    }
  }, [initialValues, initialValues.labelLanguage]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values) => {
          submitForm(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          resetForm,
          validateForm,
          setFieldValue
        }) => {
          // console.log(errors);
          return (
            <Form>
              <Typography
                variant="h3"
                sx={{
                  pb: 1,
                  justifyContent: 'center',
                  display: {xs: 'flex'},
                  marginTop: 2
                }}
              >
                Not working yet!
              </Typography>

              <Grid container spacing={2} my={1} marginTop={3}>
                <Grid item xs={12}>
                  <TextField
                    name="url"
                    value={values.url}
                    label={t('url')}
                    multiline
                    maxRows={4}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setCurrentModule({
                          ...initialValues,
                          url: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.url && Boolean(errors.url)}
                  />

                  <FormHelperText
                    sx={{
                      '&.MuiFormHelperText-root': {
                        bgcolor: theme.palette.background.paper
                      }
                    }}
                  >
                    {`${values.url.length} / ${URL_MAX_LENGTH}`}
                  </FormHelperText>

                  <FormHelperText
                    error={touched.url && Boolean(errors.url)}
                    sx={{
                      '&.MuiFormHelperText-root': {
                        bgcolor: theme.palette.background.paper
                      }
                    }}
                  >
                    {touched.url && errors.url ? errors.url : ''}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} marginBottom={1} marginTop={1}>
                <Grid item xs={12} marginTop={3}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      name="labelLanguage"
                      value={values.labelLanguage}
                      onChange={(e) => {
                        const newLanguage = e.target.value as SupportedLanguage;
                        setFieldValue('labelLanguage', newLanguage);
                        dispatch(
                          setCurrentModule({
                            labelLanguage: newLanguage
                          })
                        );

                        const length = values.label[newLanguage].length;

                        if (newLanguage === 'de') {
                          setLabelLengthDE(length);
                        } else if (newLanguage === 'en') {
                          setLabelLengthEN(length);
                        }
                      }}
                      style={{marginLeft: '10px'}}
                    >
                      <FormControlLabel
                        value="de"
                        control={<Radio />}
                        label={t('german')}
                      />
                      <FormControlLabel
                        value="en"
                        control={<Radio />}
                        label={t('english')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} marginTop={0}>
                  <TextField
                    name={`label.${values.labelLanguage}`}
                    value={values.label[values.labelLanguage || 'de']}
                    label={t('label')}
                    onChange={(e) => {
                      handleChange(e);

                      dispatch(
                        setCurrentModule({
                          label: {
                            ...values.label,
                            [values.labelLanguage || 'de']: e.target.value
                          }
                        })
                      );

                      if (values.labelLanguage === 'de') {
                        setLabelLengthDE(e.target.value.length);
                      } else if (values.labelLanguage === 'en') {
                        setLabelLengthEN(e.target.value.length);
                      }
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    multiline
                    maxRows={2}
                  />
                  <FormHelperText
                    sx={{
                      '&.MuiFormHelperText-root': {
                        bgcolor: theme.palette.background.paper
                      }
                    }}
                  >
                    {values.labelLanguage === 'de'
                      ? `${labelLengthDE} / ${LABEL_MAX_LENGTH}`
                      : `${labelLengthEN} / ${LABEL_MAX_LENGTH}`}
                  </FormHelperText>
                  <FormHelperText
                    error
                    sx={{
                      '&.MuiFormHelperText-root': {
                        bgcolor: theme.palette.background.paper
                      }
                    }}
                  >
                    {touched.label?.de && errors.label?.de}
                  </FormHelperText>
                  <FormHelperText
                    error
                    sx={{
                      '&.MuiFormHelperText-root': {
                        bgcolor: theme.palette.background.paper
                      }
                    }}
                  >
                    {touched.label?.en && errors.label?.en}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={2}
                marginTop={4}
                justifyContent={'center'}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <Button
                    component="span"
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteImage}
                    sx={{
                      display: {xs: 'none', sm: 'flex'},
                      textTransform: 'none'
                    }}
                  >
                    {t('delete_photo')}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <label htmlFor="logo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PhotoCamera />}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('upload_photo')}
                    </Button>
                  </label>

                  <label htmlFor="phote-delete-xs">
                    <IconButton
                      aria-label={t('delete_photo')}
                      color="error"
                      onClick={handleDeleteImage}
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'}
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </label>
                  <input
                    id="logo-upload"
                    name="logo"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                  <label htmlFor="photo-upload-xs">
                    <IconButton
                      aria-label={t('upload_photo')}
                      color="secondary"
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'},
                        marginLeft: 4,
                        marginTop: '2px'
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <input
                    id="photo-upload-xs"
                    name="logo"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: {xs: 0, sm: 2}
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="subtitle1" align="center">
                      {t('module_picture')}
                    </Typography>
                    <Typography variant="subtitle1" align="center">
                      {t('optimal_dimensions', {width: 16, height: 9})}
                    </Typography>
                    {initialValues.picture && (
                      <Box
                        component="img"
                        src={initialValues.picture}
                        alt={t('module_picture')}
                        sx={{
                          maxWidth: {xs: '150px', sm: '200px'},
                          maxHeight: '100px',
                          marginTop: 3
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>

              {fileSizeError && (
                <Typography align="center" color="error" marginTop={2}>
                  {fileSizeError}
                </Typography>
              )}
              {!fileSizeError && (
                <FormHelperText
                  error
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',

                    '&.MuiFormHelperText-root': {
                      bgcolor: theme.palette.background.paper
                    }
                  }}
                >
                  {errors.picture && touched.picture && errors.picture}
                </FormHelperText>
              )}
              <Grid
                container
                direction={{xs: 'column-reverse', sm: 'row'}}
                justifyContent="center"
                spacing={2}
                sx={{
                  marginTop: {xs: 2, sm: 5},
                  marginBottom: 5
                }}
              >
                {!showOnlyTwoButtons && (
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      order: {xs: 1, sm: 0}
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        resetFormData();
                        resetForm({
                          values: initialState.currentModule
                        });
                        if (onCancel) {
                          onCancel();
                        }
                      }}
                      sx={{
                        width: {xs: '50%', sm: '80%'}
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  sm={showOnlyTwoButtons ? 6 : 4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    order: 2,
                    marginBottom: {xs: 2, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetFormData();
                      resetForm({
                        values: initialState.currentModule
                      });
                      validateForm();
                    }}
                    sx={{
                      width: {
                        xs: '50%',
                        sm: showOnlyTwoButtons ? '70%' : '80%'
                      }
                    }}
                  >
                    {t('reset')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={showOnlyTwoButtons ? 6 : 4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    order: 3
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!!fileSizeError}
                    sx={{
                      width: {
                        xs: '50%',
                        sm: showOnlyTwoButtons ? '70%' : '80%'
                      }
                    }}
                  >
                    {t('save')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default ModuleAdd;
